/**
 * Header row shared by the import progress cards (completed, completed with
 * ticket, failed): status icon, resolved source label, relative timestamp and
 * the dismiss button. Presentation only; each card owns its wrapper and body.
 */

import type { ReactNode } from 'react'

import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { resolveSyncSourceLabel } from '@/platform/sync/sync-source-label'

import { FOCUS_RING_CLASS } from './ImportRowParts'

interface ImportItemHeaderProps {
  icon: ReactNode
  sourceName: string
  /** Already formatted relative time of the completion/failure. */
  timestampLabel: string
  onDismiss: () => void
  dismissTestId: string
  /** Hover background of the dismiss button, tinted per card status. */
  dismissHoverClassName: string
}

export const ImportItemHeader = ({
  icon,
  sourceName,
  timestampLabel,
  onDismiss,
  dismissTestId,
  dismissHoverClassName,
}: ImportItemHeaderProps) => {
  const { t } = useTranslation('imports')
  // `resolveSyncSourceLabel` resolves `monitoring.unknownSource`, which lives
  // in the COMMON catalog — the imports namespace has no such key.
  const { t: tCommon } = useTranslation('common')
  const sourceLabel = resolveSyncSourceLabel(sourceName, tCommon)

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        {icon}
        <span className="font-medium text-sm text-crypto-text truncate" title={sourceLabel}>
          {sourceLabel}
        </span>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <span className="text-xs text-crypto-muted whitespace-nowrap">{timestampLabel}</span>
        <button
          type="button"
          onClick={onDismiss}
          className={`p-1 ${dismissHoverClassName} rounded ${FOCUS_RING_CLASS}`}
          aria-label={t('progressIndicator.dismiss', 'Dismiss')}
          title={t('progressIndicator.dismiss', 'Dismiss')}
          data-testid={dismissTestId}
        >
          <X className="w-3.5 h-3.5 text-crypto-muted" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
